import { observer } from 'mobx-react-lite';
import Modal from '../../modal';
import { TestIDs } from 'src/constants/testIDs.constants';
import { styleDesktop, styleMobile } from './ModalConfigurationSteps.style';
import { ConfigurationStepId } from 'src/helpers/configurationSteps.helper';
import { modalTestID } from 'src/helpers/testIDs.helper';
import { PrimaryButton } from 'src/editor-v3/components/buttons/primary-button/PrimaryButton';
import { BUTTON_LAYOUTS, BUTTON_SIZES, BUTTON_TYPES } from 'src/editor-v3/components/buttons/button/Button.constants';
import { FocusGroups } from '../../../focus-provider/FocusProvider.constants';
import { View } from 'react-native';
import TextLato from '../../../text/text-lato/TextLato';
import { brand, neutrals } from 'src/theme/colors';
import { TextIconButton } from '../../../buttons/text-icon-button/TextIconButton';
import {
  TEXT_ICON_BUTTON_SIZES,
  TEXT_ICON_BUTTON_TYPES,
} from '../../../buttons/text-icon-button/TextIconButton.constants';
import { ICON_NAMES } from '../../../icon/icon.helper';
import { CircularButton } from '../../../buttons/circular-button/CircularButton';
import {
  CIRCULAR_BUTTON_SIZES,
  CIRCULAR_BUTTON_TYPES,
} from '../../../buttons/circular-button/CircularButton.constants';
import { AvoidCuts } from './steps/AvoidCuts';
import { InfoPageProps, StepProps } from './ModalConfigurationSteps.types';
import { useModalConfigurationStepsLogic } from './ModalConfigurationSteps.logic';
import { FormatCards } from './steps/step-cards/FormatCards';
import { BandInfo, BoxStyleInfo, FormatInfo } from '@photosi/expo-photosi-sdk/build/PrintUtils';
import { BandCards } from './steps/step-cards/BandCards';
import { DynamicModalProps } from '../DynamicModal.types';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';
import { BOTTOM_SHEET_VIEWS } from 'src/store/bottom-sheet/bottomSheet.constants';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { BoxStyleCards } from './steps/step-cards/BoxStyleCards';

const InfoPage = ({ step, card, productGroup }: InfoPageProps) => {
  let Component;
  switch (step) {
    case ConfigurationStepId.BAND:
    case ConfigurationStepId.FORMAT:
      Component = AvoidCuts;
      break;
    default:
      return <></>;
  }
  return <Component card={card} productGroup={productGroup} />;
};

const Step = ({ step, onSelectCard, cards, onSelectCardMobile, productGroup }: StepProps) => {
  switch (step) {
    case ConfigurationStepId.BAND:
      return (
        <BandCards
          productGroup={productGroup}
          cards={cards as BandInfo[]}
          onSelectCard={onSelectCard}
          onSelectCardMobile={onSelectCardMobile}
        />
      );
    case ConfigurationStepId.FORMAT:
      return (
        <FormatCards
          productGroup={productGroup}
          cards={cards as FormatInfo[]}
          onSelectCard={onSelectCard}
          onSelectCardMobile={onSelectCardMobile}
        />
      );
    case ConfigurationStepId.BOX_STYLE:
      return (
        <BoxStyleCards
          productGroup={productGroup}
          cards={cards as BoxStyleInfo[]}
          onSelectCard={onSelectCard}
          onSelectCardMobile={onSelectCardMobile}
        />
      );
    default:
      return <></>;
  }
};

export const ModalConfigurationSteps = observer((props: DynamicModalProps) => {
  const {
    i18n,
    dynamicModalVisible,
    openBottomSheet,
    closeModal,
    showInfoPage,
    setShowInfoPage,
    actualStep,
    cards,
    onSelectCard,
    onSelectCardMobile,
    showCtaInfo,
    onConfirmStep,
    onGoBack,
    stepsDone,
    id,
    productGroup,
  } = useModalConfigurationStepsLogic(props);

  const { isDesktop } = useResponsiveScreen();
  const style = isDesktop ? styleDesktop : styleMobile;

  return (
    <Modal
      visible={dynamicModalVisible}
      onClose={closeModal}
      showHeader={false}
      style={style.modal}
      style_container={style.content}
      closeOnClickOutside={false}
      testID={TestIDs.CONFIGURATION_STEPS}
      type={props.type}
      id={id}
      isInstant={!isDesktop}
    >
      {actualStep ? (
        <>
          <View style={style.header}>
            <View style={style.headerLeft}>
              {stepsDone.length !== 0 && !showInfoPage && (
                <CircularButton
                  onPress={async () => onGoBack()}
                  size={CIRCULAR_BUTTON_SIZES.SMALL}
                  type={CIRCULAR_BUTTON_TYPES.ONLY_ICON}
                  iconName={ICON_NAMES.ArrowLeft1}
                  focusGroup={FocusGroups.MODAL}
                  testID={modalTestID(TestIDs.CONFIGURATION_STEPS, TestIDs.BACK)}
                  focusId={modalTestID(TestIDs.CONFIGURATION_STEPS, TestIDs.BACK)}
                  focusOrder={0}
                  iconStyle={{ color: brand.main.water }}
                  style={[{ marginLeft: 18 }]}
                />
              )}
            </View>
            <View style={[style.titleWrapper, { flex: 2 }]}>
              <TextLato style={style.title}>{i18n.t(`modal-configuration-steps:${actualStep}-title`)}</TextLato>
            </View>
            <View style={style.headerRight} />
          </View>
          {showInfoPage && isDesktop && (
            <>
              <CircularButton
                onPress={async () => {
                  amplitude.trackEvent(AnalyticsEvents.lookup.backClick, {
                    lookupcode: actualStep,
                    position: 'avoid_cut',
                    target: actualStep,
                  });
                  setShowInfoPage(false);
                }}
                size={CIRCULAR_BUTTON_SIZES.SMALL}
                type={CIRCULAR_BUTTON_TYPES.ONLY_ICON}
                iconName={ICON_NAMES.ArrowLeft1}
                focusGroup={FocusGroups.MODAL}
                testID={modalTestID(TestIDs.CONFIGURATION_STEPS, TestIDs.BACK)}
                focusId={modalTestID(TestIDs.CONFIGURATION_STEPS, TestIDs.BACK)}
                focusOrder={0}
                iconStyle={{ color: brand.main.water }}
                style={{ position: 'absolute', left: 50 }}
              />
              <InfoPage
                step={actualStep}
                card={(cards as FormatInfo[]).filter(elem => elem.isSuggested)[0]}
                productGroup={productGroup}
              />
            </>
          )}
          {!showInfoPage && (
            <>
              {!isDesktop && showCtaInfo && (
                <View
                  style={{
                    width: '100%',
                    alignItems: 'center',
                    paddingVertical: 16,
                    backgroundColor: neutrals.coldGrays.ice,
                  }}
                >
                  <TextIconButton
                    size={TEXT_ICON_BUTTON_SIZES.LARGE}
                    type={TEXT_ICON_BUTTON_TYPES.ICON_LEFT}
                    text={i18n.t('modal-configuration-steps:avoid-cuts')}
                    iconName={ICON_NAMES.Info1}
                    iconStyle={{ color: brand.main.water }}
                    textStyle={{ color: brand.main.water }}
                    innerStyle={{ flexDirection: 'row', gap: 8 }}
                    focusGroup={FocusGroups.MODAL}
                    testID={modalTestID(TestIDs.CONFIGURATION_STEPS, TestIDs.INFO)}
                    focusId={modalTestID(TestIDs.CONFIGURATION_STEPS, TestIDs.INFO)}
                    focusOrder={cards.length + 2}
                    onPress={async () => {
                      openBottomSheet({
                        bottomSheetView: BOTTOM_SHEET_VIEWS.AvoidCuts,
                        suggestedFormatCard: (cards as FormatInfo[]).filter(elem => elem.isSuggested)[0],
                        bottomSheetData: {
                          productGroup,
                        },
                      });
                    }}
                    focusStyle={{ borderBottomWidth: 2, borderBottomColor: brand.main.water }}
                  />
                </View>
              )}
              <Step
                step={actualStep}
                onSelectCard={onSelectCard}
                onSelectCardMobile={onSelectCardMobile}
                cards={cards}
                productGroup={productGroup}
              />
              {showCtaInfo && isDesktop && (
                <View style={{ width: '100%', alignItems: 'flex-start', paddingHorizontal: 32 }}>
                  <TextIconButton
                    size={TEXT_ICON_BUTTON_SIZES.LARGE}
                    type={TEXT_ICON_BUTTON_TYPES.ICON_LEFT}
                    text={i18n.t('modal-configuration-steps:avoid-cuts')}
                    iconName={ICON_NAMES.Info1}
                    iconStyle={{ color: brand.main.water }}
                    textStyle={{ color: brand.main.water }}
                    innerStyle={{ flexDirection: 'row', gap: 8 }}
                    focusGroup={FocusGroups.MODAL}
                    testID={modalTestID(TestIDs.CONFIGURATION_STEPS, TestIDs.INFO)}
                    focusId={modalTestID(TestIDs.CONFIGURATION_STEPS, TestIDs.INFO)}
                    focusOrder={cards.length + 2}
                    onPress={async () => {
                      amplitude.trackEvent(AnalyticsEvents.lookup.infoClick, {
                        lookupcode: actualStep,
                        info_type: 'avoid_cuts',
                      });
                      setShowInfoPage(true);
                    }}
                    focusStyle={{ borderBottomWidth: 2, borderBottomColor: brand.main.water }}
                  />
                </View>
              )}
              {isDesktop && (
                <PrimaryButton
                  onPress={async () => onConfirmStep()}
                  style={style.button}
                  textStyle={style.buttonText}
                  testID={modalTestID(TestIDs.CONFIGURATION_STEPS, TestIDs.CONTINUE)}
                  focusId={modalTestID(TestIDs.CONFIGURATION_STEPS, TestIDs.CONTINUE)}
                  focusGroup={FocusGroups.MODAL}
                  text={i18n.t('modal-configuration-steps:continue')}
                  focusOrder={cards.length + 3}
                  size={BUTTON_SIZES.REGULAR}
                  type={BUTTON_TYPES.DEFAULT}
                  layout={BUTTON_LAYOUTS.CENTER}
                />
              )}
            </>
          )}
        </>
      ) : null}
    </Modal>
  );
});
